import { ChevronDown, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * A dropdown you can type into — for filter lists too long to scan (stacks, concepts). One value or
 * none; "" means no filter. Plain `<select>` stays the choice for short fixed lists.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  label,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  label: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();

  const shown = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return needle ? options.filter((o) => o.toLowerCase().includes(needle)) : options;
  }, [options, find]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  function pick(next: string) {
    onChange(next);
    setFind("");
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, shown.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && shown[active] !== undefined) {
      e.preventDefault();
      pick(shown[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={root} className={cn("relative", className)}>
      <div className="border-bank-border bg-bank-surface focus-within:border-bank-accent flex h-9 items-center rounded-md border">
        <input
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          className="min-w-0 flex-1 bg-transparent px-2.5 text-sm outline-none"
          placeholder={value || placeholder}
          value={open ? find : value}
          onFocus={() => setOpen(true)}
          onChange={(e) => (setFind(e.target.value), setActive(0), setOpen(true))}
          onKeyDown={onKey}
        />
        {value ? (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            className="text-bank-muted hover:text-bank-ink px-1.5"
            onClick={() => pick("")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        ) : (
          <ChevronDown className="text-bank-muted mr-2 size-3.5" aria-hidden="true" />
        )}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="border-bank-border bg-bank-surface absolute z-20 mt-1 max-h-60 w-full min-w-48 overflow-y-auto rounded-md border py-1 shadow-md"
        >
          {shown.length === 0 && <li className="text-bank-muted px-3 py-1.5 text-xs">No match.</li>}
          {shown.map((o, i) => (
            <li
              key={o}
              role="option"
              aria-selected={o === value}
              className={cn(
                "cursor-pointer truncate px-3 py-1.5 text-sm",
                i === active && "bg-bank-accent-wash",
                o === value && "text-bank-accent-ink font-medium",
              )}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => (e.preventDefault(), pick(o))}
            >
              {o}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
